const ENCODED_WORD_PATTERN = /=\?([^?]+)\?([bBqQ])\?([^?]*)\?=/g;
const MAX_ENCODED_WORD_LENGTH = 75;
const Q_PREFIX = "=?UTF-8?Q?";
const Q_SUFFIX = "?=";

export function encodeWords(value: string): string {
  if (/^[\x20-\x7e]*$/.test(value)) {
    return value;
  }

  const chunks: string[] = [];
  let chunk = "";

  for (const character of value) {
    const candidate = chunk + character;
    const encoded = encodeQText(candidate);

    if (Q_PREFIX.length + encoded.length + Q_SUFFIX.length > MAX_ENCODED_WORD_LENGTH && chunk) {
      chunks.push(`${Q_PREFIX}${encodeQText(chunk)}${Q_SUFFIX}`);
      chunk = character;
    } else {
      chunk = candidate;
    }
  }

  if (chunk) {
    chunks.push(`${Q_PREFIX}${encodeQText(chunk)}${Q_SUFFIX}`);
  }

  return chunks.join(" ");
}

export function decodeWords(value: string): string {
  // Whitespace between adjacent encoded words is ignored.
  const normalized = value.replace(/(\?=)[ \t\r\n]+(?==\?)/g, "$1$2");

  return normalized.replace(
    ENCODED_WORD_PATTERN,
    (original, charset: string, encoding: string, encodedText: string) => {
      try {
        const bytes =
          encoding.toUpperCase() === "B" ? decodeBase64(encodedText) : decodeQText(encodedText);

        return new TextDecoder(charset).decode(bytes);
      } catch {
        return original;
      }
    },
  );
}

function encodeQText(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let result = "";

  for (const byte of bytes) {
    if (byte === 0x20) {
      // Spaces are represented by underscores in RFC 2047 Q-encoding.
      result += "_";
    } else if (isSafeQByte(byte)) {
      result += String.fromCharCode(byte);
    } else {
      result += `=${byte.toString(16).toUpperCase().padStart(2, "0")}`;
    }
  }

  return result;
}

function isSafeQByte(byte: number): boolean {
  // Printable ASCII excluding ",", "=", "?", and "_".
  return (
    byte >= 0x21 && byte <= 0x7e && byte !== 0x2c && byte !== 0x3d && byte !== 0x3f && byte !== 0x5f
  );
}

function decodeQText(value: string): Uint8Array {
  const bytes: number[] = [];

  for (let index = 0; index < value.length; index += 1) {
    const character = value[index]!;

    if (character === "_") {
      bytes.push(0x20);
      continue;
    }

    if (character === "=" && /^[0-9A-Fa-f]{2}$/.test(value.slice(index + 1, index + 3))) {
      bytes.push(Number.parseInt(value.slice(index + 1, index + 3), 16));
      index += 2;
      continue;
    }

    bytes.push(character.charCodeAt(0));
  }

  return new Uint8Array(bytes);
}

function decodeBase64(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return bytes;
}
